import { Grid, withStyles } from '@material-ui/core';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { formValueSelector } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import ApyDisclosure from '../../components/disclosure/apyDisclosure';
import { GhostButton } from '../../components/buttons/buttons';
import { NAO_FORM_ID } from '../../utilities/accountOpeningFlowType';
import ButtonLink from '../../components/links/buttonLink';
import { SynchronyInfo } from '../../components/cms/blockText.constants';
import { SectionHeader } from '../../components/typography/typography';
import type { StyleProps } from '../../utilities/types';
import type { ReduxState } from '../../reducers';
import styles from './welcome.styles';
import { promoCodeSession } from '../../utilities/promoProduct';
import FormWithImage from '../../components/formWithImage/formWithImage';
import Routes, { QueryParams } from '../routes/routes.constants';
import { ANALYTICS_WELCOME_PAGE } from '../../analytics/actions';
import { WelcomePageBtn } from '../../components/cms/buttonText.constants';
import { getSearchParam } from '../../utilities/url';
import { buildRegisterNewUserUrl } from '../../utilities/authentication';
import { useEffectOnce } from '../../utilities/reactHooks';
import type { Promotion, ProductCode, Product } from '../products/products.reducer';
import PromoProductFlashMessage from '../../components/flashMessages/promoProductFlashMessage';
import Error from '../../components/error/error';
import {
  FlashMessageText,
  FlashMessageVariant,
} from '../../components/flashMessage/flashMessage.constants';
import IgniteFlashMessage from '../../components/flashMessage/IgniteFlashMessage';
import fetchAndValidateProductTypes from '../products/products.actions';
import { PRODUCT_SECTION_ID } from '../../form.constants';
import { DyEventNames } from '../../components/cms/dynamicYield.constants';
import DynamicYieldEvent from '../../components/openNewAccountFormFlow/dynamicYieldEvent';
import getImageSrc from '../../utilities/getImageSrc';
import ImagesFileNames from '../../images';

type DispatchProps = {
  setAnalyticsPageView: () => void;
  fetchProductTypes: () => void;
};

type StateProps = {
  activePromotion: Promotion | null | undefined;
  hasProductError: boolean;
  isProductsLoading: boolean;
  isPromotionCodeValid?: boolean;
  selectedProductCode?: ProductCode | string;
  products: Product[];
};

type Props = DispatchProps & StyleProps & StateProps;

const mapDispatchToProps = (dispatch: ThunkDispatch<null, null, AnyAction>): DispatchProps => ({
  setAnalyticsPageView: () => {
    dispatch({ type: ANALYTICS_WELCOME_PAGE });
  },
  fetchProductTypes: () => dispatch(fetchAndValidateProductTypes()),
});

const selector = formValueSelector(NAO_FORM_ID);

const mapStateToProps = (state: ReduxState): StateProps => {
  let sectionFields: Record<string, string> = {};
  if (selector) sectionFields = selector(state, PRODUCT_SECTION_ID) || {};

  return {
    activePromotion: state.products.activePromotion,
    hasProductError: state.products.error,
    isProductsLoading: state.products.isLoading,
    isPromotionCodeValid: state.products.validPromotionCode,
    products: state.products.productTypes.products,
    selectedProductCode: sectionFields.productType,
  };
};

const renderFlashMessage = (activePromotion: Promotion) => () => {
  if (activePromotion) {
    return <PromoProductFlashMessage promotion={activePromotion} />;
  }
  return (
    <IgniteFlashMessage variant={FlashMessageVariant.ERROR} data-test="promo-product-errorMessage">
      {FlashMessageText.PROMO_CODE_NOT_VALID}
    </IgniteFlashMessage>
  );
};

export function WelcomeContainer(props: Props) {
  const {
    activePromotion,
    classes,
    fetchProductTypes,
    hasProductError,
    isProductsLoading,
    isPromotionCodeValid,
    setAnalyticsPageView,
    selectedProductCode,
    products,
  } = props;
  useEffect(() => {
    setAnalyticsPageView();
  }, [setAnalyticsPageView]);

  const rightPanelImageUrl = getImageSrc(ImagesFileNames.accountOpeningJpg);
  const rightPanelImageUrlLg = getImageSrc(ImagesFileNames.accountOpening_2xJpg);
  const rightPanelImageUrlXl = getImageSrc(ImagesFileNames.accountOpening_3xJpg);

  const uisCode = getSearchParam(window.location, QueryParams.affinityCode);
  const promoCode = getSearchParam(window.location, QueryParams.promoCode);
  const query = new URLSearchParams();
  if (uisCode) query.append(QueryParams.affinityCode, uisCode);
  if (promoCode) query.append(QueryParams.promoCode, promoCode);

  useEffectOnce(() => {
    const promoCodeParam: string = sessionStorage.getItem(promoCodeSession.PROMO_CODE_PARAM);
    if (promoCodeParam) {
      sessionStorage.removeItem(promoCodeSession.PROMO_CODE_PARAM);
    }
    if (promoCode) {
      fetchProductTypes();
    }
  });

  const getSelectedProduct = (selected: string | null | undefined, productList: Product[]) =>
    productList.find((product) => product.displayCode === selected);

  const selectedProduct = getSelectedProduct(selectedProductCode, products);

  const showPromoFlashMessage =
    (activePromotion || (promoCode && isPromotionCodeValid === false)) &&
    (!selectedProduct || (selectedProduct && !!selectedProduct.promotion));

  return hasProductError ? (
    <Error
      message={FlashMessageText.GENERIC_ERROR}
      center
      onClick={() => {
        fetchProductTypes();
      }}
    />
  ) : (
    <Grid container className={classes.parentContainer} spacing={0} alignItems="stretch">
      <FormWithImage
        imageUrl={rightPanelImageUrl}
        imageUrlLg={rightPanelImageUrlLg}
        imageUrlXl={rightPanelImageUrlXl}
        showMarketingMessage
        ctaText={`Is this your first ${SynchronyInfo.BANKNAME} account?`}
        useLoadingAnimation
        loading={isProductsLoading}
        flashMessageRenderProp={showPromoFlashMessage && renderFlashMessage(activePromotion)}
      >
        <Helmet>
          <title>Open an Account - {SynchronyInfo.BANKNAME}</title>
          <meta
            name="description"
            content={`${SynchronyInfo.BANKNAME} can help you reach your financial goals. Open an account today.`}
          />
        </Helmet>
        <DynamicYieldEvent event={DyEventNames.APPLICATION_PRE_VIEW} />
        <Grid item>
          <ButtonLink
            to={`${Routes.NAO}?${query.toString()}`}
            id="newAccount"
            data-test="new-account-button"
            data-track-title="new_account_open"
            className={classes.welcomeButton}
          >
            {WelcomePageBtn.CONTINUE_TO_NAO}
          </ButtonLink>
          <ButtonLink
            to={`${Routes.EAO}?${query.toString()}`}
            data-track-title="existing_user"
            className={classes.welcomeButton}
          >
            {WelcomePageBtn.CONTINUE_TO_EAO}
          </ButtonLink>
          <ButtonLink
            to={buildRegisterNewUserUrl(Routes.EAO)}
            external
            data-track-title="register"
            className={classes.welcomeButton}
            component={GhostButton}
          >
            {WelcomePageBtn.CREATE_USERNAME}
          </ButtonLink>
        </Grid>
        <Grid item className={classes.welcomeDescription}>
          <SectionHeader data-test="txt-types-of-accounts">
            We are pleased to offer personal accounts online. If you are looking to open an IRA,
            Trust, Custodial or Non-Personal account please call us at {SynchronyInfo.PHONE}.
          </SectionHeader>
        </Grid>
        <ApyDisclosure classes={classes} />
      </FormWithImage>
    </Grid>
  );
}

const connectedComponent = connect(mapStateToProps, mapDispatchToProps)(WelcomeContainer);
const styledComponent = withStyles(styles)(connectedComponent);
export default styledComponent;
